/**
 * Performance monitoring utilities for production
 * Tracks key metrics and logs performance issues
 */

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private readonly maxMetrics = 100; // Keep last 100 metrics in memory
  private webVitalsInitialized = false;

  /**
   * Mark the start of a performance measurement
   */
  markStart(name: string) {
    if ('performance' in window) {
      performance.mark(`${name}-start`);
    }
  }

  /**
   * Mark the end of a performance measurement and calculate duration
   */
  markEnd(name: string, metadata?: Record<string, any>): number {
    if (!('performance' in window)) return 0;

    try {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);
      
      const measure = performance.getEntriesByName(name, 'measure')[0];
      const duration = measure?.duration || 0;

      // Log slow operations in production
      if (import.meta.env.PROD && duration > 1000) {
        console.warn(`Slow operation detected: ${name} took ${duration.toFixed(2)}ms`);
      }

      // Store metric
      this.addMetric({
        name,
        value: duration,
        timestamp: Date.now(),
        metadata
      });

      // Cleanup
      performance.clearMarks(`${name}-start`);
      performance.clearMarks(`${name}-end`);
      performance.clearMeasures(name);

      return duration;
    } catch (error) {
      console.error('Performance measurement error:', error);
      return 0;
    }
  }

  /**
   * Measure an async function's execution time
   */
  async measureAsync<T>(
    name: string,
    fn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    this.markStart(name);
    try {
      const result = await fn();
      this.markEnd(name, metadata);
      return result;
    } catch (error) {
      this.markEnd(name, { ...metadata, error: true });
      throw error;
    }
  }

  /**
   * Measure a synchronous function's execution time
   */
  measure<T>(
    name: string,
    fn: () => T,
    metadata?: Record<string, any>
  ): T {
    this.markStart(name);
    try {
      const result = fn();
      this.markEnd(name, metadata);
      return result;
    } catch (error) {
      this.markEnd(name, { ...metadata, error: true });
      throw error;
    }
  }

  private addMetric(metric: PerformanceMetric) {
    this.metrics.push(metric);
    
    // Keep only last N metrics to prevent memory leaks
    if (this.metrics.length > this.maxMetrics) {
      this.metrics.shift();
    }
  }

  /**
   * Get performance metrics for analysis
   */
  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  /**
   * Get average duration for a specific metric name
   */
  getAverageDuration(name: string): number {
    const relevant = this.metrics.filter(m => m.name === name);
    if (relevant.length === 0) return 0;
    
    const sum = relevant.reduce((acc, m) => acc + m.value, 0);
    return sum / relevant.length;
  }

  /**
   * Clear all stored metrics
   */
  clear() {
    this.metrics = [];
    if ('performance' in window) {
      performance.clearMarks();
      performance.clearMeasures();
    }
  }

  /**
   * Monitor Core Web Vitals
   */
  monitorWebVitals() {
    if (!('PerformanceObserver' in window)) return;
    if (this.webVitalsInitialized) return;
    this.webVitalsInitialized = true;

    try {
      const supported: string[] = (PerformanceObserver as any).supportedEntryTypes || [];

      // Largest Contentful Paint (LCP)
      if (supported.includes('largest-contentful-paint')) {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as any;
          const lcp = lastEntry?.renderTime || lastEntry?.loadTime || 0;

          if (lcp) {
            this.addMetric({ name: 'web-vital-lcp', value: lcp, timestamp: Date.now() });
            if (lcp > 2500) console.warn('Poor LCP detected:', lcp);
          }
        });
        // Use modern API with type + buffered to avoid unsupported entryTypes warnings
        (lcpObserver as any).observe({ type: 'largest-contentful-paint', buffered: true });
      }

      // First Input Delay (FID)
      if (supported.includes('first-input')) {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            const fid = (entry.processingStart || 0) - (entry.startTime || 0);
            this.addMetric({ name: 'web-vital-fid', value: fid, timestamp: Date.now() });
            if (fid > 100) console.warn('Poor FID detected:', fid);
          });
        });
        (fidObserver as any).observe({ type: 'first-input', buffered: true });
      }

      // Cumulative Layout Shift (CLS)
      if (supported.includes('layout-shift')) {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.addMetric({ name: 'web-vital-cls', value: clsValue, timestamp: Date.now() });
          if (clsValue > 0.1) console.warn('Poor CLS detected:', clsValue);
        });
        (clsObserver as any).observe({ type: 'layout-shift', buffered: true });
      }

    } catch (error) {
      console.error('Failed to initialize web vitals monitoring:', error);
    }
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Auto-start web vitals monitoring in production
if (import.meta.env.PROD && typeof window !== 'undefined') {
  performanceMonitor.monitorWebVitals();
}
